import { useEffect, useMemo, useRef, useState } from 'react'

import { Box, Square } from '@chakra-ui/react'
import { LineStyle } from '@editor/interfaces/canvas'
import { CircleMeshProps } from '@editor/interfaces/canvas/circleMesh'
import { LINE_WIDTH_TO_GAP_RATIO } from '@editor/pages/projects/blueprint-editor/constants'
import { zeroPad } from '@editor/services/Util'
import { RootState, useAppDispatch } from '@editor/store/app'
import { setEditingElementId, toggleSelectedElementIds } from '@editor/store/tool'
import { StylesProvider } from '@infraloop/common'
import { Html } from '@react-three/drei'
import { merge } from 'lodash'
import { useSelector } from 'react-redux'
import { DoubleSide, Matrix4, Mesh, MeshStandardMaterial, Quaternion, Vector3 } from 'three'
import { Line2, LineGeometry } from 'three-stdlib'

import { DEFAULT_LINE_MATERIAL } from '../Polyline/constants'
import useEventHandlers from './hooks/useEventHandlers'

const maxAngleDeg = 5 // smaller = smoother
const segments = Math.ceil(360 / maxAngleDeg)

const CircleMesh = (props: CircleMeshProps) => {
  const {
    id,
    transformation,
    offsetTransformation,
    radius,

    color = '#ffffff',
    outline,
    opacity = 1,
    label,

    isWorking = false,
    isEditing = false,
    selectable = false,
    visible = true,
    onComplete,

    renderOrder,
    depthTest = true,
    depthWrite = true,
  } = props

  // Store
  const dispatch = useAppDispatch()
  const labelFontSize = useSelector((state: RootState) => state.toolUtil.core.labelFontSize)
  const hoveredElementId = useSelector((state: RootState) => state.toolUtil.core.hoveredElementId)
  const selectedElementIds = useSelector((state: RootState) => state.toolUtil.core.selectedElementIds)
  const editingElementId = useSelector((state: RootState) => state.toolUtil.core.editingElementId)
  const currentAnchorPosition = useSelector((state: RootState) => state.toolUtil.core.currentAnchorPosition)
  const currentFillColor = useSelector((state: RootState) => state.toolUtil.core.currentFillColor)
  const currentStrokeColor = useSelector((state: RootState) => state.toolUtil.core.currentStrokeColor)

  // Refs
  const meshRef = useRef<Mesh | null>(null)
  const isResizing = useRef(false)
  const resizeStartPosition = useRef<Vector3 | null>(null)
  const resizeNewRadius = useRef<number | null>(null)

  const tempCornerMatrix = useRef<Matrix4>(new Matrix4())
  const tempMoveOffset = useRef<Vector3>(new Vector3())

  // Derived state
  const isHoverSelected = useMemo(
    () => hoveredElementId === id || selectedElementIds.includes(id),
    [hoveredElementId, id, selectedElementIds],
  )

  // Decompose transformation matrix
  const { position, quaternion, matrix } = useMemo(() => {
    const pos = new Vector3()
    const quat = new Quaternion()
    const mtx = new Matrix4().fromArray(transformation)

    mtx.decompose(pos, quat, new Vector3())

    return {
      position: pos,
      quaternion: quat,
      matrix: mtx,
    }
  }, [transformation])
  const [circlePosition, setCirclePosition] = useState<Vector3>(position)

  const { position: offsetPosition } = useMemo(() => {
    if (!offsetTransformation) return { position: new Vector3(), quaternion: new Quaternion(), matrix: new Matrix4() }

    const pos = new Vector3()
    const quat = new Quaternion()
    const mtx = new Matrix4().fromArray(offsetTransformation)

    mtx.decompose(pos, quat, new Vector3())

    return {
      position: pos,
      quaternion: quat,
      matrix: mtx,
    }
  }, [offsetTransformation])

  /**
   * Event handlers
   */
  const {
    isDraggingRef,
    lastPositionRef,
    lastAnchorPositionRef,
    handleMouseEnter,
    handleMouseLeave,
    handleMouseDown,
    handleClick,
  } = useEventHandlers(props)

  // Label Configs
  const labelConfig = useMemo(
    (): CircleMeshProps['label'] =>
      label
        ? merge(
            {},
            {
              color: 'black',
              backgroundColor: 'yellow',
              fontSize: labelFontSize,
            },
            label,
          )
        : undefined,
    [label, labelFontSize],
  )

  // Calculate radius if working
  const effectiveRadius = useMemo(() => {
    if ((!isWorking && !isEditing) || !currentAnchorPosition || isDraggingRef.current) {
      return radius
    }

    if (isEditing && isResizing.current) {
      if (!resizeStartPosition.current) {
        resizeStartPosition.current = new Vector3(...currentAnchorPosition)
      }

      const halfDiagonal = new Vector3(...currentAnchorPosition).sub(circlePosition).length()
      resizeNewRadius.current = halfDiagonal / Math.sqrt(2)
      return resizeNewRadius.current
    }

    if (isWorking) {
      return circlePosition.distanceTo(new Vector3(...currentAnchorPosition))
    }

    return resizeNewRadius.current || radius
  }, [isDraggingRef, radius, resizeNewRadius, currentAnchorPosition, isWorking, isEditing, isResizing, circlePosition])

  const cornerPoints = useMemo(
    () => [
      [-effectiveRadius - (outline?.thickness || 0) / 2, -effectiveRadius - (outline?.thickness || 0) / 2, 0],
      [effectiveRadius + (outline?.thickness || 0) / 2, -effectiveRadius - (outline?.thickness || 0) / 2, 0],
      [effectiveRadius + (outline?.thickness || 0) / 2, effectiveRadius + (outline?.thickness || 0) / 2, 0],
      [-effectiveRadius - (outline?.thickness || 0) / 2, effectiveRadius + (outline?.thickness || 0) / 2, 0],
      [-effectiveRadius - (outline?.thickness || 0) / 2, -effectiveRadius - (outline?.thickness || 0) / 2, 0],
    ],
    [effectiveRadius, outline?.thickness],
  )

  const cornerHandlePoints = useMemo(() => {
    const mat = tempCornerMatrix.current.copy(matrix).setPosition(circlePosition)
    return cornerPoints.map((point) => new Vector3(...point).applyMatrix4(mat))
  }, [cornerPoints, matrix, circlePosition, tempCornerMatrix])

  /**
   * Outline line
   */
  const outlineObject = useMemo(() => {
    if (!outline?.color) return null

    // Create points around the circle, centered at `center`
    const circlePoints: number[] = []

    for (let i = 0; i <= segments; i += 1) {
      const theta = (i / segments) * Math.PI * 2
      circlePoints.push(effectiveRadius * Math.cos(theta), effectiveRadius * Math.sin(theta), 0)
    }

    const geometry = new LineGeometry()
    geometry.setPositions(circlePoints)

    const outlineMat = DEFAULT_LINE_MATERIAL.clone()

    outlineMat.dashed = outline.style === LineStyle.Dashed
    outlineMat.color.set(outline.color) // Use white when set as transparent since 'transparent' is not a valid color
    outlineMat.linewidth = outline.thickness || 0
    outlineMat.dashSize = outlineMat.linewidth * LINE_WIDTH_TO_GAP_RATIO
    outlineMat.gapSize = outlineMat.linewidth * LINE_WIDTH_TO_GAP_RATIO

    const outlineLn = new Line2(geometry, outlineMat)
    outlineLn.computeLineDistances()
    outlineLn.scale.set(1, 1, 1)

    // Set the position and rotation of the outline to match the circle mesh
    outlineLn.applyMatrix4(matrix)

    return outlineLn
  }, [effectiveRadius, outline, matrix])

  /**
   * Container outline used for selection state and resize handles.
   */
  const [containerOutline] = useState(() => {
    const geometry = new LineGeometry()
    geometry.setPositions(cornerPoints.flat())

    const outlineMat = DEFAULT_LINE_MATERIAL.clone()

    outlineMat.dashed = false
    outlineMat.color.set('#0bc5ea') // Use white when set as transparent since 'transparent' is not a valid color
    outlineMat.linewidth = 0.002

    const outlineLn = new Line2(geometry, outlineMat)
    outlineLn.computeLineDistances()
    outlineLn.scale.set(1, 1, 1)

    // Set the position and rotation of the outline to match the circle mesh
    outlineLn.applyMatrix4(matrix)

    return outlineLn
  })

  /**
   * Update geometry and outline when being dragged.
   */
  useEffect(() => {
    if (!editingElementId || !isDraggingRef.current || !currentAnchorPosition) return

    if (!lastAnchorPositionRef.current) {
      lastAnchorPositionRef.current = new Vector3(...currentAnchorPosition)
      return
    }

    const offset = tempMoveOffset.current.fromArray(currentAnchorPosition).sub(lastAnchorPositionRef.current)
    lastAnchorPositionRef.current.fromArray(currentAnchorPosition)

    setCirclePosition((prev) => {
      const newPosition = prev.clone().add(offset)

      if (!lastPositionRef.current) {
        lastPositionRef.current = newPosition.clone()
      } else {
        lastPositionRef.current.copy(newPosition)
      }

      return newPosition
    })
  }, [currentAnchorPosition, editingElementId, isDraggingRef, lastPositionRef, lastAnchorPositionRef, tempMoveOffset])

  /**
   * Update containerOutline when effectiveRadius changes during resizing.
   */
  useEffect(() => {
    if (!containerOutline || !isResizing.current) return

    containerOutline.geometry.setPositions(cornerPoints.flat())
  }, [cornerPoints, containerOutline, isResizing])

  /**
   * Update the mesh material color when the current fill color changes.
   * This is used for real-time updates of the fill color while the user is selecting it to
   * avoid unnecessary re-renders.
   *
   * The color set here _should not_ be the final color. It still must come from the `color` prop.
   */
  useEffect(() => {
    if (
      !meshRef.current ||
      !(meshRef.current.material instanceof MeshStandardMaterial) ||
      !currentFillColor ||
      (!selectedElementIds.includes(id) && !isWorking)
    )
      return

    meshRef.current.visible = true // mesh is invisible by default due to no color.
    meshRef.current.material.color.set(currentFillColor)
  }, [currentFillColor, selectedElementIds, id, isWorking])
  useEffect(() => {
    if (!outlineObject || !currentStrokeColor || (!selectedElementIds.includes(id) && !isWorking)) return

    outlineObject?.material.color.set(currentStrokeColor)
  }, [outlineObject, currentStrokeColor, selectedElementIds, id, isWorking])

  useEffect(() => {
    const handleMouseUp = (ev: MouseEvent) => {
      if (!isEditing || isDraggingRef.current) {
        resizeNewRadius.current = null
        return
      }

      ev.stopPropagation()

      dispatch(setEditingElementId(undefined))
      dispatch(toggleSelectedElementIds({ ids: [id], selected: true }))

      if (resizeNewRadius.current !== null) {
        onComplete?.(transformation, resizeNewRadius.current)
      }

      resizeStartPosition.current = null
      isResizing.current = false
    }

    window.addEventListener('mouseup', handleMouseUp)
    return () => {
      window.removeEventListener('mouseup', handleMouseUp)
    }
  }, [isDraggingRef, isEditing, id, transformation, onComplete, dispatch])

  return (
    <group visible={opacity > 0 && visible} name={`circle-mesh-${id}`} position={offsetPosition}>
      <mesh
        position={circlePosition}
        quaternion={quaternion}
        ref={meshRef}
        visible={color !== 'transparent'}
        onPointerEnter={selectable ? handleMouseEnter : undefined}
        onPointerLeave={handleMouseLeave} // need to be able to unset states even if not selectable
        onPointerDown={selectable ? handleMouseDown : undefined}
        onClick={selectable ? handleClick : undefined}
        renderOrder={renderOrder}
      >
        <circleGeometry args={[effectiveRadius, segments]} />
        <meshStandardMaterial
          color={color === 'transparent' ? 'white' : color}
          transparent
          opacity={opacity}
          side={DoubleSide}
          depthTest={depthTest}
          depthWrite={depthWrite}
        />
      </mesh>

      {/* Outline */}
      {outlineObject && <primitive object={outlineObject} position={circlePosition} />}

      {/* Container outline */}
      {containerOutline && selectable ? (
        <primitive object={containerOutline} renderOrder={1} position={circlePosition} visible={isHoverSelected} />
      ) : null}

      {cornerHandlePoints.map((pos, index) => (
        <Html
          key={`${id}-${zeroPad(index, 3)}`}
          position={pos}
          style={{
            transform: 'translateX(-50%) translateY(-50%)',
            display: (isHoverSelected || isEditing) && selectable ? 'block' : 'none',
          }}
        >
          <StylesProvider>
            <Square
              size="10px"
              border="1px solid #0bc5ea"
              bg="white"
              cursor="grab"
              onMouseDown={() => {
                isResizing.current = true
                dispatch(setEditingElementId(id))
              }}
            />
          </StylesProvider>
        </Html>
      ))}

      {/* Label */}
      {!!label && labelConfig ? (
        <Html
          position={circlePosition}
          style={{ transform: 'translateX(-50%) translateY(-50%)', pointerEvents: 'none' }}
          zIndexRange={[1, 9]}
        >
          <StylesProvider>
            <Box
              backgroundColor={labelConfig.backgroundColor}
              px={2}
              // Convert the multiplier into a percentage-based font size for CSS styling
              fontSize={`${80 * (labelConfig.fontSize || 1)}%`}
              fontWeight="bold"
              color={labelConfig.color}
              whiteSpace="nowrap"
            >
              {labelConfig.message}
            </Box>
          </StylesProvider>
        </Html>
      ) : null}
    </group>
  )
}

export default CircleMesh
